import { useState, useRef, useCallback, useEffect, forwardRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Paperclip, 
  X, 
  AlertCircle, 
  Upload, 
  FileText, 
  Image, 
  File,
  Info,
  Building2,
  Tag,
  MessageSquare,
  Clock,
  Sparkles,
  AtSign
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { AssuntosService, ConfiguracoesService, DepartamentosService, PrioridadesService, TicketsService } from '@/services';
import { Departamento, Prioridade, Assunto } from '@/types';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AIAssistantModal from '@/components/AIAssistantModal';
import TemplateDropdown from '@/components/Tickets/TemplateDropdown';
import UserMention from '@/components/Tickets/UserMention';
import { convertQuillMentionsToBackendFormat } from '@/utils/mentionQuillHelper';
import QuillEditor from '@/components/Tickets/QuillEditor';
import DuplicateTicketModal from '@/components/Tickets/DuplicateTicketModal';

declare global {
  interface JQuery {
    select2(options?: any): JQuery;
    select2(method: string): JQuery;
  }
}

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false,
  },
  keyboard: {
    bindings: {
      tab: false,
    }
  },
  history: {
    delay: 1000,
    maxStack: 500,
    userOnly: true
  }
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet',
  'align',
  'link', 'image',
  'mention'
];

const quillStyles = `
  .quill-wrapper {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #d1d5db;
    transition: all 0.2s ease;
  }
  
  .dark .quill-wrapper {
    border-color: #374151;
  }
  
  .quill-wrapper:hover {
    border-color: #9ca3af;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  .dark .quill-wrapper:hover {
    border-color: #4b5563;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
  }
  
  .quill-wrapper .ql-toolbar {
    border-top: none;
    border-left: none;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 12px 16px;
  }
  
  .dark .quill-wrapper .ql-toolbar {
    border-bottom-color: #374151;
    background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  }
  
  .quill-wrapper .ql-container {
    border: none;
    font-size: 14px;
    line-height: 1.6;
    background: white;
  }
  
  .dark .quill-wrapper .ql-container {
    background: #1f2937;
  }
  
  .quill-wrapper .ql-editor {
    min-height: 140px;
    padding: 20px;
    color: #1e293b;
    font-size: 14px;
    line-height: 1.7;
  }
  
  .dark .quill-wrapper .ql-editor {
    color: #f3f4f6;
  }
  
  .quill-wrapper .ql-editor.ql-blank::before {
    color: #94a3b8;
    font-style: italic;
    font-size: 14px;
    left: 20px;
  }
  
  .dark .quill-wrapper .ql-editor.ql-blank::before {
    color: #6b7280;
  }
  
  .quill-wrapper .ql-editor:focus {
    outline: none;
  }
  
  .quill-wrapper .ql-snow .ql-picker {
    color: #475569;
  }
  
  .dark .quill-wrapper .ql-snow .ql-picker {
    color: #9ca3af;
  }
  
  .quill-wrapper .ql-snow .ql-stroke {
    stroke: #6b7280;
  }
  
  .dark .quill-wrapper .ql-snow .ql-stroke {
    stroke: #9ca3af;
  }
  
  .quill-wrapper .ql-snow .ql-fill {
    fill: #6b7280;
  }
  
  .dark .quill-wrapper .ql-snow .ql-fill {
    fill: #9ca3af;
  }
  
  .quill-wrapper .ql-snow .ql-picker-options {
    background-color: white;
    border: 1px solid #d1d5db;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
  
  .dark .quill-wrapper .ql-snow .ql-picker-options {
    background-color: #1f2937;
    border-color: #374151;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.2);
  }
  
  .quill-wrapper .ql-snow .ql-picker-item {
    color: #374151;
    padding: 8px 16px;
    transition: all 0.2s ease;
  }
  
  .dark .quill-wrapper .ql-snow .ql-picker-item {
    color: #d1d5db;
  }
  
  .quill-wrapper .ql-snow .ql-picker-item:hover {
    background-color: #f3f4f6;
    color: #1f2937;
  }
  
  .dark .quill-wrapper .ql-snow .ql-picker-item:hover {
    background-color: #374151;
    color: #f3f4f6;
  }
  
  .quill-wrapper .ql-snow .ql-picker-item.ql-selected {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    color: white;
  }
  
  .quill-wrapper .ql-editor p {
    margin: 0 0 16px 0;
  }
  
  .quill-wrapper .ql-editor p:last-child {
    margin-bottom: 0;
  }
  
  .quill-wrapper .ql-editor ul, .quill-wrapper .ql-editor ol {
    margin: 16px 0;
    padding-left: 28px;
  }
  
  .quill-wrapper .ql-editor li {
    margin: 6px 0;
  }
  
  .quill-wrapper .ql-editor a {
    color: #3b82f6;
    text-decoration: underline;
    transition: color 0.2s ease;
  }
  
  .dark .quill-wrapper .ql-editor a {
    color: #60a5fa;
  }
  
  .quill-wrapper .ql-editor a:hover {
    color: #2563eb;
  }
  
  .dark .quill-wrapper .ql-editor a:hover {
    color: #93c5fd;
  }
  
  .quill-wrapper .ql-editor h1,
  .quill-wrapper .ql-editor h2,
  .quill-wrapper .ql-editor h3 {
    margin: 20px 0 12px 0;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.3;
  }
  
  .dark .quill-wrapper .ql-editor h1,
  .dark .quill-wrapper .ql-editor h2,
  .dark .quill-wrapper .ql-editor h3 {
    color: #f9fafb;
  }
  
  .quill-wrapper .ql-editor h1 {
    font-size: 22px;
  }
  
  .quill-wrapper .ql-editor h2 {
    font-size: 20px;
  }
  
  .quill-wrapper .ql-editor h3 {
    font-size: 18px;
  }
  
  .quill-wrapper .ql-editor blockquote {
    border-left: 4px solid #3b82f6;
    margin: 20px 0;
    padding: 12px 20px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    font-style: italic;
    color: #475569;
    border-radius: 0 8px 8px 0;
  }
  
  .dark .quill-wrapper .ql-editor blockquote {
    border-left-color: #60a5fa;
    background: linear-gradient(135deg, #1e2937 0%, #111827 100%);
    color: #cbd5e1;
  }
  
  .quill-wrapper .ql-editor pre {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    color: #e2e8f0;
    padding: 16px;
    border-radius: 8px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    overflow-x: auto;
    border: 1px solid #334155;
  }
  
  .dark .quill-wrapper .ql-editor pre {
    background: linear-gradient(135deg, #0f172a 0%, #020617 100%);
    border-color: #1e293b;
  }
  
  .quill-wrapper .ql-editor code {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    color: #dc2626;
    padding: 4px 8px;
    border-radius: 6px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    border: 1px solid #cbd5e1;
  }
  
  .dark .quill-wrapper .ql-editor code {
    background: linear-gradient(135deg, #1e2937 0%, #111827 100%);
    color: #f87171;
    border-color: #374151;
  }
  
  .quill-wrapper .ql-editor table {
    border-collapse: collapse;
    width: 100%;
    margin: 20px 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  .dark .quill-wrapper .ql-editor table {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
  }
  
  .quill-wrapper .ql-editor table td,
  .quill-wrapper .ql-editor table th {
    border: 1px solid #e2e8f0;
    padding: 12px 16px;
    text-align: left;
  }
  
  .dark .quill-wrapper .ql-editor table td,
  .dark .quill-wrapper .ql-editor table th {
    border-color: #374151;
  }
  
  .quill-wrapper .ql-editor table th {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    font-weight: 600;
    color: #1e293b;
  }
  
  .dark .quill-wrapper .ql-editor table th {
    background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
    color: #f9fafb;
  }
  
  .quill-wrapper .ql-editor table tr:nth-child(even) {
    background-color: #f8fafc;
  }
  
  .dark .quill-wrapper .ql-editor table tr:nth-child(even) {
    background-color: #1f2937;
  }
  
  .quill-wrapper .ql-editor table tr:hover {
    background-color: #f1f5f9;
  }
  
  .dark .quill-wrapper .ql-editor table tr:hover {
    background-color: #374151;
  }
  
  /* Botões da toolbar com melhor aparência */
  .quill-wrapper .ql-toolbar button {
    transition: all 0.2s ease;
  }
  
  .quill-wrapper .ql-toolbar button:hover {
    color: #3b82f6;
    background-color: #eff6ff;
  }
  
  .dark .quill-wrapper .ql-toolbar button:hover {
    color: #60a5fa;
    background-color: #1e3a8a;
  }
  
  .quill-wrapper .ql-toolbar button.ql-active {
    color: #3b82f6;
    background-color: #dbeafe;
  }
  
  .dark .quill-wrapper .ql-toolbar button.ql-active {
    color: #60a5fa;
    background-color: #1e40af;
  }
  
  /* Picker labels no dark mode */
  .dark .quill-wrapper .ql-snow .ql-picker-label {
    color: #d1d5db;
  }
  
  /* Picker items selecionados no dark mode */
  .dark .quill-wrapper .ql-snow .ql-picker-item.ql-selected {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    color: white;
  }
`;

interface FormData {
  departamento_ids: string[]; // Array de IDs dos departamentos
  assuntos_por_departamento: { [key: string]: string }; // Assunto específico para cada departamento
  prioridade_id: string;
  descricao: string;
  anexos: File[];
  assuntos_por_departamento_data: { [key: string]: Assunto[] }; // Armazenar assuntos por departamento
}

export default function NewTicket() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const quillRef = useRef<ReactQuill>(null);
  const templateButtonRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [prioridades, setPrioridades] = useState<Prioridade[]>([]);
  const [assuntos, setAssuntos] = useState<Assunto[]>([]);
  const [formData, setFormData] = useState<FormData>({
    departamento_ids: [],
    assuntos_por_departamento: {},
    prioridade_id: '',
    descricao: '',
    anexos: [],
    assuntos_por_departamento_data: {}
  });
  const [showAIModal, setShowAIModal] = useState(false);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateTickets, setDuplicateTickets] = useState<any[]>([]);
  const [pendingSubmit, setPendingSubmit] = useState<(() => void) | null>(null);
  
  // Estados para configurações de arquivos
  const [configuracoes, setConfiguracoes] = useState({
    arquivos_permitidos: 'jpg, png, pdf, doc, docx, xls, xlsx',
    maximo_arquivos: '5',
    tamanho_maximo: '10'
  });

  useEffect(() => {
    loadFormData();
    loadConfiguracoes();
  }, []);


  // Carregar assuntos quando os departamentos mudarem
  useEffect(() => {
    // Filtrar IDs vazios antes de processar
    const departamentosValidos = formData.departamento_ids.filter(id => id && id.trim() !== '');
    
    if (departamentosValidos.length > 0) {
      // Carregar assuntos de todos os departamentos selecionados
      const carregarAssuntosMultiplos = async () => {
        try {
          const todosAssuntos: Assunto[] = [];
          const assuntosPorDepartamento: { [key: string]: Assunto[] } = {};
          
          for (const departamentoId of departamentosValidos) {
            const assuntosDept = await AssuntosService.getAssuntosParaNovoTicket(parseInt(departamentoId));
            
            // Armazenar assuntos por departamento
            assuntosPorDepartamento[departamentoId] = assuntosDept;
            todosAssuntos.push(...assuntosDept);
          }
          
          // Remover duplicatas baseado no ID
          const assuntosUnicos = todosAssuntos.filter((assunto, index, self) => 
            index === self.findIndex(a => a.id === assunto.id)
          );
          
          setAssuntos(todosAssuntos);
          
          // Armazenar assuntos por departamento no estado para uso posterior
          setFormData(prev => ({ 
            ...prev, 
            assuntos_por_departamento: {},
            // Adicionar assuntos por departamento ao estado
            assuntos_por_departamento_data: assuntosPorDepartamento
          }));
        } catch (error) {
          setAssuntos([]);
        }
      };
      
      carregarAssuntosMultiplos();
    } else {
      setAssuntos([]);
      // Resetar assunto quando não há departamentos selecionados
      setFormData(prev => ({ ...prev, assuntos_por_departamento: {} }));
    }
  }, [formData.departamento_ids]);

  const loadFormData = async () => {
    try {
      const [deptResponse, prioResponse] = await Promise.all([
        DepartamentosService.getDepartamentos(),
        PrioridadesService.getPrioridades()
      ]);
      setDepartamentos(deptResponse);
      setPrioridades(prioResponse);
    } catch (error) {
      toast.error('Erro ao carregar dados do formulário');
    }
  };

  const loadConfiguracoes = async () => {
    try {
      const response = await ConfiguracoesService.getConfiguracoes();
      if (Array.isArray(response)) {
        const config = response.reduce((acc: any, item: any) => {
          acc[item.nome] = item.valor;
          return acc;
        }, {});
        
        setConfiguracoes({
          arquivos_permitidos: config.arquivos_permitidos || 'jpg, png, pdf, doc, docx, xls, xlsx',
          maximo_arquivos: config.maximo_arquivos || '5',
          tamanho_maximo: config.tamanho_maximo || '10'
        });
      }
    } catch (error) {
      // Usar valores padrão se houver erro
    }
  };

  const loadAssuntosPorDepartamento = async (departamentoId: number) => {
    try {
      const response = await AssuntosService.getAssuntosParaNovoTicket(departamentoId);
      setAssuntos(response);
    } catch (error) {
      toast.error('Erro ao carregar assuntos do departamento');
      setAssuntos([]);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (formData.departamento_ids.length === 0) {
      newErrors.departamento_ids = 'Pelo menos um departamento deve ser selecionado';
    }
    
    // Verificar se cada departamento tem um assunto selecionado
    if (formData.departamento_ids.length > 0) {
      const departamentosSemAssunto = formData.departamento_ids.filter(deptId => 
        !formData.assuntos_por_departamento[deptId] || formData.assuntos_por_departamento[deptId] === ''
      );
      
      if (departamentosSemAssunto.length > 0) {
        newErrors.assuntos_por_departamento = `Os seguintes departamentos não têm assunto selecionado: ${departamentosSemAssunto.map(deptId => {
          const dept = departamentos.find(d => d.id.toString() === deptId);
          return dept?.nome || `ID: ${deptId}`;
        }).join(', ')}`;
      }
    }
    
    if (!formData.prioridade_id) {
      newErrors.prioridade_id = 'Prioridade é obrigatória';
    }
    
    if (!formData.descricao || formData.descricao.trim() === '<p><br></p>' || formData.descricao.trim() === '') {
      newErrors.descricao = 'Descrição é obrigatória';
    } else {
      // Remover tags HTML para contar caracteres reais
      const textContent = formData.descricao.replace(/<[^>]*>/g, '').trim();
      if (textContent.length < 20) {
        newErrors.descricao = 'Descrição deve ter pelo menos 20 caracteres';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createTickets = async () => {
    setIsSubmitting(true);
    
    try {
      // Preparar dados para envio ao backend
      const departamentosData = formData.departamento_ids
        .filter(deptId => deptId && deptId.trim() !== '')
        .map(deptId => {
          const assuntoId = formData.assuntos_por_departamento[deptId];
          const assunto = assuntos.find(a => a.id.toString() === assuntoId);
          
          return {
            departamento_id: parseInt(deptId),
            assunto: assunto ? assunto.nome : ''
          };
        })
        .filter(dept => dept.assunto); // Filtrar apenas departamentos com assunto válido

      if (departamentosData.length === 0) {
        throw new Error('Nenhum departamento válido com assunto selecionado');
      }

      // Converter menções do formato Quill para formato backend antes de enviar
      let descricaoProcessada = formData.descricao;
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        if (quill) {
          const quillHTML = quill.root.innerHTML;
          descricaoProcessada = convertQuillMentionsToBackendFormat(quillHTML);
        } else {
          // Fallback: usar o conteúdo do estado diretamente
          descricaoProcessada = convertQuillMentionsToBackendFormat(formData.descricao);
        }
      } else {
        descricaoProcessada = convertQuillMentionsToBackendFormat(formData.descricao);
      }

      // Dados para envio ao backend
      const ticketData = {
        departamentos: departamentosData,
        prioridade_id: parseInt(formData.prioridade_id),
        descricao: descricaoProcessada
      };

      // Criar tickets através da API
      const response: any = await TicketsService.createTicket(ticketData);
      
      // Se houver anexos, fazer upload deles para cada ticket criado
      if (formData.anexos.length > 0) {
        // Determinar quais tickets receberão anexos
        let ticketsParaUpload: any[] = [];
        
        // Verificar múltiplos tickets primeiro (formato: { tickets: [...] })
        if (response.tickets && Array.isArray(response.tickets) && response.tickets.length > 0) {
          ticketsParaUpload = response.tickets.filter((t: any) => t && (t.id || t.ID));
        } 
        // Verificar ticket único no formato novo (formato: { ticket: {...} })
        else if (response.ticket && typeof response.ticket === 'object') {
          const ticketId = response.ticket.id || response.ticket.ID;
          if (ticketId) {
            ticketsParaUpload = [response.ticket];
          }
        } 
        // Verificar se a resposta é diretamente um ticket (formato antigo: { id: ..., assunto: ... })
        else if (response.id || response.ID) {
          const ticketId = response.id || response.ID;
          if (ticketId) {
            ticketsParaUpload = [response];
          }
        }
        // Verificar se a resposta é um array direto
        else if (Array.isArray(response) && response.length > 0) {
          ticketsParaUpload = response.filter((t: any) => t && (t.id || t.ID));
        }
        // Tentar encontrar ticket em qualquer lugar da resposta
        else {
          // Buscar recursivamente por objetos com propriedade 'id' que parecem ser tickets
          const findTickets = (obj: any, depth: number = 0): any[] => {
            if (depth > 3) return []; // Limitar profundidade para evitar loops infinitos
            
            if (Array.isArray(obj)) {
              return obj.filter((item: any) => item && (item.id || item.ID));
            }
            
            if (obj && typeof obj === 'object') {
              // Se o objeto tem 'id' e parece ser um ticket
              const ticketId = obj.id || obj.ID;
              if (ticketId && (obj.assunto || obj.departamento_id)) {
                return [obj];
              }
              
              // Buscar em propriedades do objeto
              for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                  const found = findTickets(obj[key], depth + 1);
                  if (found.length > 0) {
                    return found;
                  }
                }
              }
            }
            
            return [];
          };
          
          const foundTickets = findTickets(response);
          if (foundTickets.length > 0) {
            ticketsParaUpload = foundTickets;
          }
        }
        
        if (ticketsParaUpload.length === 0) {
          toast.error('O ticket foi criado, mas os anexos não foram enviados. Por favor, adicione os anexos manualmente.');
        } else {
        // Aguardar um pouco para garantir que a resposta do ticket foi criada no backend
          // Seguindo o mesmo padrão usado na resposta do ticket
          await new Promise(resolve => setTimeout(resolve, 800));
        
        for (const ticket of ticketsParaUpload) {
          try {
            const ticketId = ticket.id || ticket.ID;
            if (!ticketId) {
              continue;
            }
            
              // Usar o mesmo formato usado na resposta do ticket (createTicketResponse)
              // O PHP espera anexos[0], anexos[1], etc. para processar como array
            const formDataFiles = new FormData();
            formData.anexos.forEach((file, index) => {
              formDataFiles.append(`anexos[${index}]`, file);
            });
            
              // Fazer upload dos anexos (o backend agora cria a resposta automaticamente se não existir)
            await TicketsService.uploadTicketFiles(Number(ticketId), formDataFiles);
            toast.success(`Anexos enviados com sucesso para o ticket #${ticketId}`);
            
          } catch (uploadError: any) {
            const errorMessage = uploadError.response?.data?.error || uploadError.message || 'Erro desconhecido';
            toast.error(`Erro ao enviar anexos para o ticket #${ticket.id || ticket.ID}: ${errorMessage}`);
            // Continuar com os outros tickets mesmo se houver erro no upload
            }
          }
        }
      }
      
      // Mostrar mensagem de sucesso e redirecionar
      // Usar a mesma lógica de detecção de tickets para navegação
      if (response.tickets && Array.isArray(response.tickets)) {
        if (response.tickets.length === 1) {
          const ticketId = response.tickets[0].id || response.tickets[0].ID;
          toast.success('Ticket criado com sucesso!');
          navigate(`/tickets/${ticketId}`);
        } else {
          toast.success(`${response.tickets.length} tickets criados com sucesso!`);
          navigate('/');
        }
      } else if (response.ticket && typeof response.ticket === 'object') {
        const ticketId = response.ticket.id || response.ticket.ID;
        if (ticketId) {
          // Formato de ticket único (quando apenas um departamento é selecionado)
          toast.success('Ticket criado com sucesso!');
          navigate(`/tickets/${ticketId}`);
        } else {
          toast.success('Ticket criado com sucesso!');
          navigate('/');
        }
      } else if (response.id || response.ID) {
        const ticketId = response.id || response.ID;
        // Fallback para resposta antiga (compatibilidade)
        toast.success('Ticket criado com sucesso!');
        navigate(`/tickets/${ticketId}`);
      } else if (Array.isArray(response) && response.length > 0) {
        // Formato array direto
        if (response.length === 1) {
          const ticketId = response[0].id || response[0].ID;
          toast.success('Ticket criado com sucesso!');
          navigate(`/tickets/${ticketId}`);
        } else {
          toast.success(`${response.length} tickets criados com sucesso!`);
          navigate('/');
        }
      } else {
        // Fallback genérico - tentar encontrar ID na resposta
        const ticketId = response.ticket?.id || response.ticket?.ID || response.id || response.ID || 
                         (Array.isArray(response) && (response[0]?.id || response[0]?.ID));
        if (ticketId) {
          toast.success('Ticket criado com sucesso!');
          navigate(`/tickets/${ticketId}`);
        } else {
          toast.success('Ticket criado com sucesso!');
          navigate('/');
        }
      }
      
    } catch (error: any) {
      toast.error(error.message || 'Erro ao criar tickets');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Preparar dados para verificação de duplicados
    const departamentosData = formData.departamento_ids
      .filter(deptId => deptId && deptId.trim() !== '')
      .map(deptId => {
        const assuntoId = formData.assuntos_por_departamento[deptId];
        const assunto = assuntos.find(a => a.id.toString() === assuntoId);
        
        return {
          departamento_id: parseInt(deptId),
          assunto: assunto ? assunto.nome : ''
        };
      })
      .filter(dept => dept.assunto);

    if (departamentosData.length === 0) {
      toast.error('Nenhum departamento válido com assunto selecionado');
      return;
    }

    // Converter menções do formato Quill para formato backend antes de verificar
    let descricaoProcessada = formData.descricao;
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill) {
        const quillHTML = quill.root.innerHTML;
        descricaoProcessada = convertQuillMentionsToBackendFormat(quillHTML);
      } else {
        descricaoProcessada = convertQuillMentionsToBackendFormat(formData.descricao);
      }
    } else {
      descricaoProcessada = convertQuillMentionsToBackendFormat(formData.descricao);
    }

    try {
      // Verificar duplicados antes de criar
      const duplicateCheck = await TicketsService.verificarDuplicados({
        departamentos: departamentosData,
        descricao: descricaoProcessada
      });

      if (duplicateCheck.tem_duplicados && duplicateCheck.duplicados.length > 0) {
        // Mostrar modal de confirmação
        setDuplicateTickets(duplicateCheck.duplicados);
        setShowDuplicateModal(true);
        // Armazenar função para criar tickets após confirmação
        setPendingSubmit(() => createTickets);
        return;
      }

      // Se não houver duplicados, criar diretamente
      await createTickets();
    } catch (error: any) {
      // Se houver erro na verificação, criar mesmo assim
      await createTickets();
    }
  };

  const handleConfirmDuplicate = () => {
    setShowDuplicateModal(false);
    if (pendingSubmit) {
      pendingSubmit();
      setPendingSubmit(null);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleFiles = (files: FileList) => {
    // Verificar limite máximo de arquivos
    const maxFiles = parseInt(configuracoes.maximo_arquivos) || 5;
    if (formData.anexos.length + files.length > maxFiles) {
      toast.error(`Máximo de ${maxFiles} arquivos permitidos. Você já tem ${formData.anexos.length} arquivo(s).`);
      return;
    }

    const validFiles = Array.from(files).filter(file => {
      const maxSize = parseInt(configuracoes.tamanho_maximo) * 1024 * 1024; // Converter MB para bytes
      const allowedExtensions = configuracoes.arquivos_permitidos.split(',').map(ext => ext.trim().toLowerCase());
      
      // Mapear extensões para MIME types
      const mimeTypeMap: { [key: string]: string[] } = {
        'jpg': ['image/jpeg'],
        'jpeg': ['image/jpeg'],
        'png': ['image/png'],
        'gif': ['image/gif'],
        'pdf': ['application/pdf'],
        'txt': ['text/plain'],
        'doc': ['application/msword'],
        'docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        'xls': ['application/vnd.ms-excel'],
        'xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
      };

      // Verificar se a extensão do arquivo está permitida
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        toast.error(`Tipo de arquivo ${file.name} não é permitido. Tipos aceitos: ${configuracoes.arquivos_permitidos}`);
        return false;
      }

      // Verificar MIME type
      const allowedMimeTypes = mimeTypeMap[fileExtension] || [];
      if (allowedMimeTypes.length > 0 && !allowedMimeTypes.includes(file.type)) {
        toast.error(`Tipo de arquivo ${file.name} não é válido`);
        return false;
      }

      if (file.size > maxSize) {
        toast.error(`Arquivo ${file.name} é muito grande. Tamanho máximo: ${configuracoes.tamanho_maximo}MB`);
        return false;
      }
      
      return true;
    });

    setFormData(prev => ({
      ...prev,
      anexos: [...prev.anexos, ...validFiles]
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      anexos: prev.anexos.filter((_, i) => i !== index)
    }));
  };

  const handleAITextImprovement = (improvedText: string) => {
    setFormData(prev => ({ ...prev, descricao: improvedText }));
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <Image className="text-blue-500" size={20} />;
    if (file.type === 'application/pdf') return <FileText className="text-red-500" size={20} />;
    return <File className="text-gray-500" size={20} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Estilos personalizados para o editor simples */}
      <style>{quillStyles}</style>
      
      {/* Estilos para select nativo múltiplo */}
      <style>{`
        /* Estilos para select nativo (fallback) */
        select[multiple] {
          border: 1px solid #d1d5db;
          border-radius: 12px;
          min-height: 120px;
          padding: 12px;
          background-color: white;
          transition: all 0.2s ease;
          font-size: 14px;
          line-height: 1.5;
        }
        
        select[multiple]:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        select[multiple] option {
          padding: 8px 12px;
          margin: 2px 0;
          border-radius: 6px;
          cursor: pointer;
        }
        
        select[multiple] option:checked {
          background-color: #dbeafe;
          color: #1e40af;
          font-weight: 500;
        }
        
        select[multiple] option:hover {
          background-color: #f3f4f6;
        }
      `}</style>
      
      {/* Header com design melhorado */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Link 
            to="/" 
            className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-all duration-200 hover:shadow-sm w-fit"
          >
            <ArrowLeft size={20} className="mr-2" />
            <span className="font-medium">Voltar aos Tickets</span>
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              <MessageSquare className="w-8 h-8 mr-3 text-blue-600 dark:text-blue-400" />
              Novo Ticket
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">Crie um novo ticket de suporte para sua solicitação</p>
          </div>
        </div>
      </div>

      {/* Form com design melhorado */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
        {/* Header do formulário */}
        <div className="bg-gradient-to-r from-blue-50 dark:from-blue-900/30 to-indigo-50 dark:to-indigo-900/30 px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <Tag className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
            Informações do Ticket
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Preencha os campos obrigatórios para criar seu ticket</p>
        </div>

        <div className="p-6 lg:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Departamento e Assunto com design melhorado */}
            <div className="space-y-6">
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                  <Building2 className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                  Departamentos *
                  {formData.departamento_ids.length > 0 && (
                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-400">
                      {formData.departamento_ids.length} selecionado{formData.departamento_ids.length > 1 ? 's' : ''}
                    </span>
                  )}
                </label>
                
                {/* Select normal com lógica de seleção múltipla */}
                <div className="space-y-3">
                  <select
                    value=""
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      // Só adicionar se o valor for válido e não estiver vazio
                      if (selectedValue && selectedValue.trim() !== '' && !formData.departamento_ids.includes(selectedValue)) {
                        setFormData(prev => ({ 
                          ...prev, 
                          departamento_ids: [...prev.departamento_ids, selectedValue],
                          assuntos_por_departamento: {} // Resetar assuntos quando mudar departamentos
                        }));
                        if (errors.departamento_ids) {
                          setErrors(prev => ({ ...prev, departamento_ids: '' }));
                        }
                        // Resetar o select para a primeira opção
                        e.target.value = "";
                      }
                    }}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 transition-all duration-200 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Selecione um departamento para adicionar</option>
                    {departamentos
                      .filter(dept => {
                        // Filtrar departamentos que já foram selecionados e garantir que não há IDs vazios
                        const deptIdStr = dept.id.toString();
                        return !formData.departamento_ids.includes(deptIdStr) && deptIdStr.trim() !== '';
                      })
                      .map(dept => (
                        <option key={dept.id} value={dept.id}>
                          {dept.nome}
                        </option>
                      ))
                    }
                  </select>
                  
                  {/* Lista dos departamentos selecionados */}
                  {formData.departamento_ids.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Departamentos selecionados:</p>
                      <div className="flex flex-wrap gap-2">
                        {formData.departamento_ids
                          .filter(deptId => deptId && deptId.trim() !== '') // Filtrar IDs vazios
                          .map((deptId) => {
                            // Converter para número para comparação correta
                            const deptIdNum = parseInt(deptId);
                            const dept = departamentos.find(d => d.id === deptIdNum);
                            return (
                              <span
                                key={deptId}
                                className="inline-flex items-center gap-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-400 rounded-lg text-sm font-medium"
                              >
                                <Building2 className="w-4 h-4" />
                                {dept ? dept.nome : `Carregando... (ID: ${deptId})`}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      departamento_ids: prev.departamento_ids.filter(id => id !== deptId),
                                      assuntos_por_departamento: {
                                        ...prev.assuntos_por_departamento,
                                        [deptId]: undefined
                                      }
                                    }));
                                  }}
                                  className="ml-1 hover:bg-red-200 dark:hover:bg-red-900/50 hover:text-red-800 dark:hover:text-red-300 rounded-full p-1 transition-colors"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400">Selecione um departamento da lista para adicioná-lo. Clique no X para remover.</p>
                
                {errors.departamento_ids && (
                  <div className="flex items-center gap-2 mt-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/30 px-3 py-2 rounded-lg border border-red-200 dark:border-red-800">
                    <AlertCircle size={16} />
                    {errors.departamento_ids}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                  <Tag className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                  Assuntos por Departamento *
                </label>
                
                {/* Mensagem sobre assuntos por departamento */}
                {formData.departamento_ids.length > 1 && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800 dark:text-amber-300">
                        <p className="font-medium mb-1">Assuntos por Departamento</p>
                        <p>Cada departamento deve ter seu próprio assunto selecionado. Os tickets serão criados com assuntos específicos para cada equipe.</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Interface para assuntos por departamento */}
                {formData.departamento_ids
                  .filter(deptId => deptId && deptId.trim() !== '') // Filtrar IDs vazios
                  .map((deptId) => {
                    // Converter para número para comparação correta
                    const deptIdNum = parseInt(deptId);
                    const dept = departamentos.find(d => d.id === deptIdNum);
                    
                    // Usar assuntos específicos deste departamento
                    const assuntosDept = formData.assuntos_por_departamento_data[deptId] || [];
                    
                    return (
                      <div key={deptId} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {dept ? dept.nome : `Carregando... (ID: ${deptId})`}
                        </label>
                        <select
                          value={formData.assuntos_por_departamento[deptId] || ''}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              assuntos_por_departamento: {
                                ...prev.assuntos_por_departamento,
                                [deptId]: e.target.value
                              }
                            }));
                            if (errors.assuntos_por_departamento) {
                              setErrors(prev => ({ ...prev, assuntos_por_departamento: '' }));
                            }
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 transition-all duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                            errors.assuntos_por_departamento ? 'border-red-500 dark:border-red-600 focus:border-red-500 dark:focus:border-red-600' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                          }`}
                          required
                        >
                          <option value="">Selecione um assunto</option>
                          {assuntosDept.length > 0 ? (
                            assuntosDept.map(assunto => (
                              <option key={assunto.id} value={assunto.id}>{assunto.nome}</option>
                            ))
                          ) : (
                            <option value="" disabled>Carregando assuntos...</option>
                          )}
                        </select>
                      </div>
                    );
                  })}
              </div>
              
              {errors.assuntos_por_departamento && (
                <div className="flex items-center gap-2 mt-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/30 px-3 py-2 rounded-lg border border-red-200 dark:border-red-800">
                  <AlertCircle size={16} />
                  {errors.assuntos_por_departamento}
                </div>
              )}
            </div>

            {/* Prioridade com design melhorado */}
            <div className="space-y-3">
              <label htmlFor="prioridade_id" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                <Clock className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                Prioridade *
              </label>
              <select
                id="prioridade_id"
                value={formData.prioridade_id}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, prioridade_id: e.target.value }));
                  if (errors.prioridade_id) {
                    setErrors(prev => ({ ...prev, prioridade_id: '' }));
                  }
                }}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 transition-all duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                  errors.prioridade_id ? 'border-red-500 dark:border-red-600 focus:border-red-500 dark:focus:border-red-600' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
                required
              >
                <option value="">Selecione uma prioridade</option>
                {prioridades.map(prioridade => (
                  <option key={prioridade.id} value={prioridade.id}>
                    {prioridade.nome}
                  </option>
                ))}
              </select>
              
              {/* Descrição da Prioridade Selecionada com design melhorado */}
              {formData.prioridade_id && (
                <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 dark:from-blue-900/30 to-indigo-50 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-800 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
                        {prioridades.find(p => p.id.toString() === formData.prioridade_id)?.nome}
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-400 leading-relaxed">
                        {prioridades.find(p => p.id.toString() === formData.prioridade_id)?.descricao}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {errors.prioridade_id && (
                <div className="flex items-center gap-2 mt-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/30 px-3 py-2 rounded-lg border border-red-200 dark:border-red-800">
                  <AlertCircle size={16} />
                  {errors.prioridade_id}
                </div>
              )}
            </div>

            {/* Descrição com design melhorado */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label htmlFor="descricao" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                  Descrição *
                </label>
                <div className="flex items-center gap-2">
                  {/* Botão de Menção */}
                  <UserMention
                    currentUserId={usuario?.id}
                    usuariosMencionadosIds={[]}
                    onMention={(userId, userName) => {
                      if (quillRef.current) {
                        const quill = quillRef.current.getEditor();
                        if (quill) {
                          const range = quill.getSelection(true);
                          
                          try {
                            // Usar o Blot customizado para inserir a menção
                            const Quill = (quill as any).constructor;
                            const Delta = Quill.import('delta');
                            const mentionDelta = new Delta()
                              .retain(range ? range.index : quill.getLength() - 1)
                              .insert('\uFEFF', { mention: { userId, userName } });
                            
                            quill.updateContents(mentionDelta, 'user');
                            quill.setSelection((range ? range.index : quill.getLength() - 1) + 1);
                          } catch (error) {
                            // Fallback: inserir como texto simples
                            const mentionText = `@${userName}`;
                            if (range) {
                              quill.insertText(range.index, mentionText);
                              quill.setSelection(range.index + mentionText.length);
                            } else {
                              const length = quill.getLength();
                              quill.insertText(length - 1, mentionText);
                              quill.setSelection(length - 1 + mentionText.length);
                            }
                          }
                        }
                      }
                    }}
                    disabled={isSubmitting}
                  />
                  
                  {/* Botão de Template */}
                  <div className="relative" ref={templateButtonRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowTemplateDropdown(!showTemplateDropdown);
                      }}
                      className={`p-2 rounded-lg transition-all duration-200 border shadow-sm hover:shadow-md ${
                        showTemplateDropdown
                          ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-600"
                          : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-600 border-gray-200 dark:border-gray-600"
                      }`}
                      title="Usar Template"
                    >
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    {showTemplateDropdown && (
                        <TemplateDropdown
                          onSelect={async (conteudo) => {
                          if (!conteudo) return;
                          
                          // Atualizar o estado
                          setFormData(prev => ({ ...prev, descricao: conteudo }));
                          
                          // Aguardar um pouco e então forçar atualização no Quill
                          setTimeout(() => {
                            if (quillRef.current) {
                              const quill = quillRef.current.getEditor();
                              if (quill && conteudo) {
                                const currentContent = quill.root.innerHTML.trim();
                                const newContent = conteudo.trim();
                                
                                if (currentContent !== newContent) {
                                  try {
                                    quill.root.innerHTML = conteudo;
                                  } catch (error) {
                                    // Erro silencioso ao aplicar template
                                  }
                                }
                              }
                            }
                          }, 100);
                          
                          setShowTemplateDropdown(false);
                        }}
                        onClose={() => setShowTemplateDropdown(false)}
                      />
                    )}
                  </div>
                  
                  {/* Botão de IA */}
                  <button
                    type="button"
                    onClick={() => setShowAIModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-sm rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                  >
                    <Sparkles className="w-4 h-4" />
                    IA
                  </button>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl overflow-hidden hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                <style>{`
                  .quill-wrapper .ql-editor {
                    min-height: 300px !important;
                  }
                `}</style>
                <QuillEditor
                  ref={quillRef}
                  value={formData.descricao}
                  onChange={(content) => {
                    setFormData(prev => ({ ...prev, descricao: content }));
                    if (errors.descricao) {
                      setErrors(prev => ({ ...prev, descricao: '' }));
                    }
                  }}
                  placeholder="Descreva detalhadamente o problema ou solicitação..."
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>
              
              <div className="flex justify-between items-center">
                {errors.descricao && (
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/30 px-3 py-2 rounded-lg border border-red-200 dark:border-red-800">
                    <AlertCircle size={16} />
                    {errors.descricao}
                  </div>
                )}
                <div className="text-sm text-gray-500 dark:text-gray-400 ml-auto bg-gray-50 dark:bg-gray-700 px-3 py-1 rounded-lg border border-gray-200 dark:border-gray-700">
                  {formData.descricao ? formData.descricao.replace(/<[^>]*>/g, '').trim().length : 0}/5000 caracteres
                </div>
              </div>
            </div>

            {/* Anexos com design melhorado */}
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                <Paperclip className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                Anexos
              </label>
              <div
                className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 ${
                  dragActive 
                    ? 'border-blue-500 dark:border-primary-400 bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-primary-500' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-900/50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept={configuracoes.arquivos_permitidos.split(',').map(ext => `.${ext.trim()}`).join(',')}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="mb-4">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Upload className="h-10 w-10 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    {dragActive ? 'Solte os arquivos aqui' : 'Clique para selecionar arquivos'}
                  </p>
                  <p className="text-base text-gray-600 dark:text-gray-400 mb-3">
                    ou arraste e solte arquivos aqui
                  </p>
                  <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 rounded-lg">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Formatos aceitos:</span>
                      <div className="flex flex-wrap gap-1 sm:gap-2">
                        {configuracoes.arquivos_permitidos.split(',').map((ext, index) => (
                          <span key={index} className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400 px-2 py-1 rounded whitespace-nowrap">
                            {ext.trim().toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center sm:text-left">Tamanho máximo: {configuracoes.tamanho_maximo}MB por arquivo</p>
                </label>
              </div>
            </div>

            {/* Lista de Arquivos com design melhorado */}
            {formData.anexos.length > 0 && (
              <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                    <Paperclip className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                    Arquivos selecionados ({formData.anexos.length}/{configuracoes.maximo_arquivos})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {formData.anexos.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-50 dark:from-gray-900/50 to-gray-100 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200"
                      >
                        <div className="w-10 h-10 bg-white dark:bg-gray-700 rounded-lg flex items-center justify-center border border-gray-200 dark:border-gray-600">
                          {getFileIcon(file)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{file.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(file.size)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="inline-flex items-center p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:border-red-300 dark:hover:border-red-800 transition-all duration-200"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Botões de Ação com design melhorado */}
            <div className="flex justify-end items-center gap-4 pt-8 border-t border-gray-200 dark:border-gray-700">
              <Link 
                to="/" 
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200 font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Cancelar
              </Link>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 dark:from-primary-500 to-blue-700 dark:to-primary-600 text-white rounded-xl hover:from-blue-700 dark:hover:from-primary-600 hover:to-blue-800 dark:hover:to-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 gap-3 font-semibold shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span className="hidden sm:inline">
                      {formData.departamento_ids.length > 1 ? 'Criando Tickets...' : 'Criando Ticket...'}
                    </span>
                    <span className="sm:hidden">
                      {formData.departamento_ids.length > 1 ? 'Criando...' : 'Criando...'}
                    </span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span className="hidden sm:inline">
                      {formData.departamento_ids.length > 1 ? 'Criar Ticket' : 'Criar Ticket'}
                    </span>
                    <span className="sm:hidden">
                      {formData.departamento_ids.length > 1 ? 'Criar' : 'Criar'}
                    </span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de Assistente de IA */}
      <AIAssistantModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onApplyText={handleAITextImprovement}
        originalText={formData.descricao}
        placeholder="Ex: Melhore este texto, torne-o mais profissional, corrija erros gramaticais, etc..."
      />

      {/* Modal de Tickets Duplicados */}
      <DuplicateTicketModal
        isOpen={showDuplicateModal}
        onClose={() => {
          setShowDuplicateModal(false);
          setPendingSubmit(null);
        }}
        onConfirm={handleConfirmDuplicate}
        duplicates={duplicateTickets}
      />
    </div>
  );
}
